import React, { useState, useEffect } from 'react';
import { Globe, Code, Bot, Clock, Wrench, Archive, X, Loader2 } from 'lucide-react';
import { GoDaddyLogo } from '../../../icons';
import apiService from '../../../../services/api';

/**
 * Tool interface for type safety
 */
export interface Tool {
  type: string;
  name?: string;
  description?: string;
  registry_model_id?: number;  // For registry tools - links to a model
}

/**
 * Model interface for registry name lookup
 */
export interface Model {
  model_id: number;
  class_name?: string;
  name?: string;
}

export interface ToolModalProps {
  tools: Tool[];
  agentName: string;
  onClose: () => void;
  models?: Model[];  // Models for looking up registry names
}

/**
 * GoDaddy tool definition from API
 */
interface GoDaddyToolDefinition {
  type: string;
  name: string;
  description: string;
  example_output: string;
  notes?: string;
}

/**
 * Individual tool item with expandable description
 * AC-640: Description truncation with expand/collapse
 */
interface ToolItemProps {
  tool: Tool;
  models?: Model[];  // For looking up registry model names
  goDaddyDefinitions?: Map<string, GoDaddyToolDefinition>;  // Fresh definitions from API
}

const DESCRIPTION_TRUNCATE_LENGTH = 100;

const ToolItem: React.FC<ToolItemProps> = ({ tool, models, goDaddyDefinitions }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // For GoDaddy tools, use fresh definition from API if available
  const getFreshDescription = (): string => {
    if (tool.type.startsWith('godaddy_') && goDaddyDefinitions) {
      const definition = goDaddyDefinitions.get(tool.type);
      if (definition) {
        return definition.description;
      }
    }
    return tool.description || '';
  };

  const description = getFreshDescription();
  const shouldTruncate = description.length > DESCRIPTION_TRUNCATE_LENGTH;
  const displayDescription = shouldTruncate && !isExpanded
    ? description.slice(0, DESCRIPTION_TRUNCATE_LENGTH)
    : description;

  // For registry tools, look up the model name
  // For GoDaddy tools, use fresh name from API
  const getDisplayName = (): string => {
    if (tool.type === 'registry' && tool.registry_model_id && models) {
      const model = models.find(m => m.model_id === tool.registry_model_id);
      if (model) {
        return model.class_name || model.name || `Registry #${tool.registry_model_id}`;
      }
      return `Registry #${tool.registry_model_id}`;
    }

    // For GoDaddy tools, use fresh name from API if available
    if (tool.type.startsWith('godaddy_') && goDaddyDefinitions) {
      const definition = goDaddyDefinitions.get(tool.type);
      if (definition) {
        return definition.name;
      }
    }

    return tool.name || tool.type;
  };

  return (
    <li className="border rounded px-3 py-3 bg-gray-50 dark:bg-gray-900">
      {/* Header row: icon, name, and badge */}
      <div className="flex items-start gap-2">
        {/* Tool icon */}
        <div className={`${getToolIconColor(tool.type)} mt-0.5 flex-shrink-0`}>
          {getToolIcon(tool.type)}
        </div>

        {/* Name and badge container */}
        <div className="flex-1 min-w-0">
          {/* Tool name - allows wrapping */}
          <div className="font-medium text-gray-800 dark:text-gray-200 break-words">
            {getDisplayName()}
          </div>

          {/* Badge on its own line for clarity */}
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border mt-1 ${getToolBadgeColors(tool.type)}`}>
            {getToolIcon(tool.type, 'w-3 h-3')}
            <span>{getToolTypeLabel(tool.type)}</span>
          </span>
        </div>
      </div>

      {/* Description */}
      {description && (
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 pl-6">
          {displayDescription}
          {shouldTruncate && (
            <>
              {!isExpanded && '...'}
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-1 text-primary-600 hover:text-primary-800 hover:underline text-xs font-medium"
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * Get icon component for a tool type
 * AC-637: Tool-type specific icons
 */
const getToolIcon = (toolType: string, sizeClass: string = 'w-4 h-4'): React.ReactNode => {
  // Check for GoDaddy tools first (AC-638)
  if (toolType.startsWith('godaddy_')) {
    return <GoDaddyLogo className={sizeClass} />;
  }

  switch (toolType) {
    case 'web_search':
      return <Globe className={sizeClass} />;
    case 'registry':
      return <Archive className={sizeClass} />;
    case 'get_current_time':
      return <Clock className={sizeClass} />;
    case 'function':
      return <Code className={sizeClass} />;
    case 'agent':
      return <Bot className={sizeClass} />;
    default:
      return <Wrench className={sizeClass} />;
  }
};

/**
 * Get icon color based on tool type
 */
const getToolIconColor = (toolType: string): string => {
  if (toolType.startsWith('godaddy_')) {
    return 'text-teal-600';
  }

  switch (toolType) {
    case 'web_search':
      return 'text-primary-600';
    case 'registry':
      return 'text-purple-600';
    case 'get_current_time':
      return 'text-yellow-600';
    case 'function':
      return 'text-green-600';
    case 'agent':
      return 'text-indigo-600';
    default:
      return 'text-orange-500';
  }
};

/**
 * Get badge colors based on tool type
 * AC-639: Colored badges by tool category
 */
const getToolBadgeColors = (toolType: string): string => {
  if (toolType.startsWith('godaddy_')) {
    return 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-700';
  }

  switch (toolType) {
    case 'web_search':
      return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700';
    case 'registry':
      return 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700';
    case 'get_current_time':
      return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700';
    case 'function':
      return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700';
    case 'agent':
      return 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600';
  }
};

/**
 * Get display label for tool type
 */
const getToolTypeLabel = (toolType: string): string => {
  if (toolType.startsWith('godaddy_')) {
    // Convert godaddy_domain_search to "Domain Search"
    const action = toolType.replace('godaddy_', '').split('_').map(
      word => word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    return action;
  }

  switch (toolType) {
    case 'web_search':
      return 'Web Search';
    case 'registry':
      return 'Registry';
    case 'get_current_time':
      return 'Time';
    case 'function':
      return 'Function';
    case 'agent':
      return 'Agent';
    default:
      return toolType;
  }
};

/**
 * ToolModal - Modal displaying agent tools with enhanced UX
 *
 * Features (from ticket requirements):
 * - AC-637: Tool-type specific icons
 * - AC-638: GoDaddy logo for GoDaddy tools
 * - AC-639: Colored badges by tool category
 * - AC-640: Description truncation with expand/collapse
 * - Single source of truth: GoDaddy tools fetch fresh definitions from API
 */
const ToolModal: React.FC<ToolModalProps> = ({ tools, agentName, onClose, models }) => {
  // State for GoDaddy tool definitions (fetched from API for single source of truth)
  const [goDaddyDefinitions, setGoDaddyDefinitions] = useState<Map<string, GoDaddyToolDefinition>>(new Map());
  const [isLoadingDefinitions, setIsLoadingDefinitions] = useState(false);

  // Check if any tools are GoDaddy tools
  const hasGoDaddyTools = tools.some(t => t.type.startsWith('godaddy_'));

  // Fetch fresh GoDaddy tool definitions from API
  useEffect(() => {
    if (!hasGoDaddyTools) return;

    const fetchDefinitions = async () => {
      try {
        setIsLoadingDefinitions(true);
        const response = await apiService.getGoDaddyToolDefinitions();

        // Create a map for quick lookup by type
        const definitionsMap = new Map<string, GoDaddyToolDefinition>();
        response.tools.forEach(tool => {
          definitionsMap.set(tool.type, tool);
        });

        setGoDaddyDefinitions(definitionsMap);
      } catch (error) {
        console.error('Failed to fetch GoDaddy tool definitions:', error);
        // Fallback to stored descriptions if API fails
      } finally {
        setIsLoadingDefinitions(false);
      }
    };

    fetchDefinitions();
  }, [hasGoDaddyTools]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-cap-dark rounded-lg shadow-lg max-w-lg w-full p-4 relative max-h-[80vh] overflow-hidden flex flex-col mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-warm-white pr-8">
          Tools for {agentName}
        </h3>

        {/* Loading indicator for GoDaddy definitions */}
        {isLoadingDefinitions && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading tool definitions...</span>
          </div>
        )}

        {/* Tool list with scroll */}
        <ul className="space-y-2 overflow-y-auto flex-1">
          {tools.map((tool, idx) => (
            <ToolItem
              key={idx}
              tool={tool}
              models={models}
              goDaddyDefinitions={goDaddyDefinitions}
            />
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ToolModal;
